import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, InjectionToken, LOCALE_ID } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';

import '@angular/common/locales/global/de';
import '@angular/common/locales/global/en';
// Declares i18n keys consumed by installed @freesixtyfive/* packages so
// extract-i18n --clean keeps them (references live in node_modules).
import {
  AUTH_LOGIN_REDIRECT,
  AUTH_STORAGE,
  provideSanctumAuthentification,
  provideXsrfConfiguration,
  sanctumInterceptor,
  xsrfInterceptor,
} from '@freesixtyfive/auth';
import {
  GOOGLE_MAPS_API_KEY,
  MAP_DEFAULT_CENTER,
  MAP_DEFAULT_ZOOM,
  provideSavedSimulationApi,
  provideSiteInsightsApi,
  provideTrafficStationsApi,
  SAVED_SIMULATION_ENDPOINT,
  SITE_BEV_HOLDERS_ENDPOINT,
  SITE_INSIGHTS_ENDPOINT,
  TRAFFIC_STATIONS_ENDPOINT,
} from '@fsf/discover';
import { LARAVEL_ENDPOINT, LaravelApi } from '@fsf/util';
import { appRoutes } from './app.routes';
import { CustomPreset, DARK_MODE_SELECTOR } from './theme/custom-preset';
import './i18n/external-keys';
export const EnvironmentToken = new InjectionToken<typeof environment>(
  'app.environment',
);

export const API_BASE = new InjectionToken<string>('api.url');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: CustomPreset,
        options: {
          // Never applied, so the navy dark palette every dark-OS user sees today stops
          // rendering and Signal Light's light mode is what ships. `true` resolves
          // to 'system', which compiles to a `prefers-color-scheme: dark` media query.
          darkModeSelector: DARK_MODE_SELECTOR,
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    }),
    provideHttpClient(withInterceptors([xsrfInterceptor, sanctumInterceptor])),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'en',
      lang: 'de',
    }),
    provideXsrfConfiguration({
      requestUrl: `${process.env.FE_API_BASE}/sanctum/csrf-cookie`,
      sendAlways: true,
    }),
    provideSanctumAuthentification({
      signInUrl: `${process.env.FE_API_BASE}/auth/login`,
      signOutUrl: `${process.env.FE_API_BASE}/auth/logout`,
      forgotPwUrl: `${process.env.FE_API_BASE}/api/auth/users/forgotPassword`,
      resetPwUrl: `${process.env.FE_API_BASE}/api/auth/users/resetPassword`,
    }),

    { provide: AUTH_LOGIN_REDIRECT, useValue: '/login' },
    { provide: AUTH_STORAGE, useValue: localStorage },
    { provide: LOCALE_ID, useValue: 'de' },
    { provide: EnvironmentToken, useValue: environment },
    { provide: GOOGLE_MAPS_API_KEY, useValue: environment.googleMaps.apiKey },
    { provide: MAP_DEFAULT_CENTER, useValue: environment.googleMaps.center },
    { provide: MAP_DEFAULT_ZOOM, useValue: environment.googleMaps.zoom },
    // First LaravelApi consumer in the workspace: the candidate-site repository. Binds
    // the client to the Laravel resource URL (`${FE_API_BASE}/api/candidate-sites`).
    {
      provide: LARAVEL_ENDPOINT,
      useValue: `${environment.apiUrl}/api/candidate-sites`,
    },
    LaravelApi,
    // The BEV-holders endpoint URL for the real SiteBevHoldersApiRepository —
    // same `environment.apiUrl` prefix as the candidate-site binding above. Keeping the
    // URL here (not in @fsf/discover) leaves the lib free of any environment dependency.
    {
      provide: SITE_BEV_HOLDERS_ENDPOINT,
      useValue: `${environment.apiUrl}/api/bev-holders`,
    },
    // The saved-simulations endpoint URL for the SavedSimulationApiRepository —
    // same `environment.apiUrl` prefix as the bindings above, kept here so @fsf/discover
    // stays free of any environment dependency.
    {
      provide: SAVED_SIMULATION_ENDPOINT,
      useValue: `${environment.apiUrl}/api/saved-simulations`,
    },
    provideSavedSimulationApi(),
    // The site-insights bundle lives under the candidate-sites resource
    // (`${apiUrl}/api/candidate-sites/{id}/insights`), so the token holds that base — kept
    // here so @fsf/discover stays free of any environment dependency.
    {
      provide: SITE_INSIGHTS_ENDPOINT,
      useValue: `${environment.apiUrl}/api/candidate-sites`,
    },
    provideSiteInsightsApi(),
    // The traffic-stations endpoint URL for the real TrafficStationApiRepository —
    // same `environment.apiUrl` prefix as the bindings above. Registered at the app root
    // (not the detail route) because the consuming `TrafficStationStore` is root-provided
    // and loads once per session, so it must resolve its repository from root.
    {
      provide: TRAFFIC_STATIONS_ENDPOINT,
      useValue: `${environment.apiUrl}/api/traffic-stations`,
    },
    provideTrafficStationsApi(),
  ],
};
